import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import userAuthContext from "./userAuthContext";
import { requestToLogin } from "../../api/api";

// ─── Token expiry helpers ─────────────────────────────────────────────────────

/**
 * Decodes a JWT payload without verifying signature (client-side read only).
 * Returns the payload object or null if invalid.
 */
const decodeToken = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

/**
 * Returns true if the JWT token is expired or invalid.
 * Adds a 10-second buffer to account for clock skew.
 */
const isTokenExpired = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return payload.exp < nowInSeconds + 10;
};

/**
 * Returns milliseconds until the token expires.
 * Returns 0 if already expired.
 */
const msUntilExpiry = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return 0;
  const expiresAt = payload.exp * 1000; // convert to ms
  return Math.max(0, expiresAt - Date.now());
};

// ─── Provider ─────────────────────────────────────────────────────────────────

const UserAuthState = ({ children }) => {
  const [user, setUser]             = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [showError, setError]       = useState("");
  const [startLoading, setLoading]  = useState(false);

  // Ref to hold the auto-logout timer so we can clear it on manual logout
  const logoutTimerRef = useRef(null);

  // ─── Auto-logout scheduler ─────────────────────────────────────────────────

  /**
   * Schedules an automatic logout when the token expires.
   * Clears any previously scheduled timer first.
   */
  const scheduleAutoLogout = useCallback((token) => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }
    const remaining = msUntilExpiry(token);
    if (remaining <= 0) return;

    logoutTimerRef.current = setTimeout(() => {
      performLogout("Your session has expired. Please sign in again.");
    }, remaining);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Core logout (shared by manual + auto) ────────────────────────────────

  /**
   * Clears session state and storage.
   * @param {string} [reason] - Optional message shown on the login page.
   */
  const performLogout = useCallback((reason = "") => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    localStorage.removeItem("user");
    if (reason) {
      // Store reason so LoginPage can show it after redirect
      sessionStorage.setItem("logout_reason", reason);
    }
    setUser(null);
  }, []);

  // ─── Init: restore session from localStorage ──────────────────────────────

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        const token = parsed?.authorization;

        if (token && !isTokenExpired(token)) {
          setUser(parsed);
          scheduleAutoLogout(token);
        } else {
          // Token expired while user was away — clean up silently
          localStorage.removeItem("user");
          sessionStorage.setItem("logout_reason", "Your session has expired. Please sign in again.");
        }
      } catch {
        localStorage.removeItem("user");
      }
    }
    setInitialized(true);
  }, [scheduleAutoLogout]);

  // ─── Cleanup timer on unmount ─────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, []);

  // ─── Login ────────────────────────────────────────────────────────────────

  const login = async (userData) => {
    // Clear any previous error before each new attempt
    setError("");

    if (!userData?.email || !userData?.password) {
      setError("Email and password are required.");
      return false;
    }

    try {
      setLoading(true);
      const response = await requestToLogin(userData);

      if (response.success) {
        const token = response.authorization;

        // Guard: server returned a token but it's already expired (clock skew edge case)
        if (!token || isTokenExpired(token)) {
          setError("Received an invalid session token. Please try again.");
          return false;
        }

        localStorage.setItem("user", JSON.stringify(response));
        sessionStorage.removeItem("logout_reason");
        setUser(response);
        scheduleAutoLogout(token);
        return true;
      } else {
        setError(response.message || "Login failed. Please check your credentials.");
        return false;
      }
    } catch (error) {
      setError("Unable to connect. Please check your connection and try again.");
      console.error("Login error:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ─── Manual logout ────────────────────────────────────────────────────────

  const logout = () => {
    performLogout();
  };

  const updateSessionUser = (nextUser) => {
    setUser((prev) => {
      if (!prev) return prev;
      const nextSession = { ...prev, user: { ...prev.user, ...nextUser } };
      localStorage.setItem("user", JSON.stringify(nextSession));
      return nextSession;
    });
  };

  // ─── Clear error when user starts a new login attempt ────────────────────

  const clearError = () => setError("");

  // ─── Context value ────────────────────────────────────────────────────────

  return (
    <userAuthContext.Provider
      value={{
        user,
        login,
        logout,
        updateSessionUser,
        clearError,
        initialized,
        showError,
        startLoading,
      }}
    >
      {children}
    </userAuthContext.Provider>
  );
};

export default UserAuthState;

// Named hook for consuming auth context
export const useAuth = () => useContext(userAuthContext);
